<template>
  <section class="business-hours-section section content-container">
    <n-loading-spinner v-if="isLoading" />

    <div class="container is-fluid" v-if="!isLoading">
      <div class="columns">
        <div class="column">
          <TitledSection class="container is-fluid island">
            <template v-slot:title>Default Business Hours</template>
            <template v-slot:subtitle>
              Define your standard operating hours for your workspace to manage customer expectations and internal
              workflows
            </template>
            <div class="columns">
              <div class="column">
                <BusinessHoursSelection
                  class="business-hour-selection"
                  v-model="businessHours"
                  @error="openingTimesError($event)"
                  :enforceOnlineDayOfWeek="true"
                />
              </div>
            </div>
          </TitledSection>

          <TitledSection class="container is-fluid holidays-section island">
            <template v-slot:title>Holidays</template>
            <template v-slot:subtitle>Specify dates where usual business hours do not apply</template>
            <template v-slot:actions>
              <n-button variant="ghost" icon="add" @click="showCreateHolidayModal">Add holiday</n-button>
            </template>
            <div>
              <n-data-table :items="holidays" :columns="getColumns()" @cellClicked="openEditHoliday">
                <template v-slot:nodata>
                  <div class="no-holidays">
                    <CalendarIcon class="icon-2x" />
                    <div class="no-holidays-message">
                      <h1>No holidays have been added</h1>
                    </div>
                  </div>
                </template>

                <template v-slot:actions="{ row }">
                  <DropDownMenu :goLeft="true" dropDownIcon="dots">
                    <a @click="showDeleteModal(row)" class="dropdown-item">
                      <DeleteIcon></DeleteIcon>
                      <span class="action-label">Delete</span>
                    </a>
                  </DropDownMenu>
                </template>
              </n-data-table>
            </div>

            <CreateHoliday
              v-if="createHolidayModalDisplayed"
              @cancel="closeCreateHolidayModal"
              @create="closeCreateHolidayModal"
            />
            <UpdateHoliday
              v-if="updateHolidayModalDisplayed"
              :holiday="selectedHoliday"
              @cancel="closeUpdateHolidayModal"
              @update="closeUpdateHolidayModal"
            />

            <ConfirmationModal
              v-if="showDeleteAttributeModal"
              confirmationTitle="Delete Holiday"
              :confirmationMessage="deleteConfirmationMessage"
              :confirm-button-label="canSelectedHolidayBeDeleted ? 'Yes' : 'Ok'"
              :show-cancel-button="canSelectedHolidayBeDeleted"
              v-on:confirm="deleteHoliday"
              v-on:cancel="showDeleteAttributeModal = false"
            />
          </TitledSection>
          <div class="save-availability">
            <n-button
              variant="primary"
              class="save-button"
              :loading="updateInProgress"
              :disabled="invalidOpeningTimes"
              @click="save"
              >Save
            </n-button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import CreateHoliday from '@/features/settings/components/CreateHoliday.vue';
import UpdateHoliday from '@/features/settings/components/UpdateHoliday.vue';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import { DELETE_HOLIDAY, LOAD_HOLIDAYS } from '@/store/holidays/actionTypes';
import moment from 'moment';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import Toast from '@/shared/services/toast';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import CalendarIcon from 'vue-material-design-icons/CalendarRange.vue';
import BusinessHoursSelection from '@/features/bots/components/businessHoursSelection/BusinessHoursSelection.vue';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { botsApi } from '@/features/bots/services/bots';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const holidays = computed(() => store.getters.holidays);
const getFlow = computed(() => store.getters.getFlow);

const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);
const loadHolidays = (forceLoad?: boolean) => store.dispatch(LOAD_HOLIDAYS, forceLoad);
const executeDelete = (holidayId: string) => store.dispatch(DELETE_HOLIDAY, holidayId);
const loadFlows = () => store.dispatch(LOAD_FLOWS);

const businessHours = ref<BusinessHoursViewModel[]>([]);
const selectedHoliday = ref<HolidayViewModel>(<HolidayViewModel>{});
const createHolidayModalDisplayed = ref(false);
const updateHolidayModalDisplayed = ref(false);
const showDeleteAttributeModal = ref(false);
const isLoading = ref(true);
const botAppInstallError = ref(false);
const updateInProgress = ref(false);
const invalidOpeningTimes = ref(false);

const isHolidayUsedInDefaultBusinessHours = computed(() =>
  businessHours.value.some((bh) => bh.holidayId === selectedHoliday.value.id),
);
const isHolidayUsedInFlows = computed(
  () => selectedHoliday.value.usedInFlowIds && selectedHoliday.value.usedInFlowIds.length > 0,
);

const usedFlowHtmlList = computed(() => {
  const flowNames = selectedHoliday.value.usedInFlowIds!.map((flowId) => getFlowDisplayName(flowId));
  let flowListHtml = '<ul style="list-style: disc outside">';
  flowListHtml += flowNames.map((flowName) => `<li>${flowName}</li>`).join('');
  flowListHtml += '</ul>';
  return flowListHtml;
});

const holidayDisplayName = computed(() => HtmlFormatter.escapeHtmlCharacters(selectedHoliday.value.name));

const canSelectedHolidayBeDeleted = computed(
  () => !isHolidayUsedInDefaultBusinessHours.value && !isHolidayUsedInFlows.value,
);

const deleteConfirmationMessage = computed(() => {
  if (canSelectedHolidayBeDeleted.value) {
    return `Are you sure you want to delete holiday <b>${holidayDisplayName.value}</b>?`;
  } else {
    let warningText = `The holiday <b>${holidayDisplayName.value}</b> cannot be deleted `;
    if (isHolidayUsedInDefaultBusinessHours.value && isHolidayUsedInFlows.value) {
      warningText += `as it's in use in your default business hours and in the following flows: ${usedFlowHtmlList.value}`;
    } else if (isHolidayUsedInDefaultBusinessHours.value) {
      warningText += `as it's in use in your default business hours.`;
    } else if (isHolidayUsedInFlows.value) {
      warningText += `as it's in use in the following flows: ${usedFlowHtmlList.value}`;
    }
    return warningText;
  }
});

onMounted(async () => {
  try {
    await loadHolidays(true);
    businessHours.value = await loadBusinessHours();
  } catch (error) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    }
  } finally {
    isLoading.value = false;
  }
});

function showCreateHolidayModal() {
  createHolidayModalDisplayed.value = true;
}

function closeCreateHolidayModal() {
  createHolidayModalDisplayed.value = false;
}

function openEditHoliday(holiday: HolidayViewModel) {
  selectedHoliday.value = holiday;
  updateHolidayModalDisplayed.value = true;
}

function closeUpdateHolidayModal() {
  updateHolidayModalDisplayed.value = false;
}

async function showDeleteModal(holiday: HolidayViewModel) {
  selectedHoliday.value = holiday;
  if (canSelectedHolidayBeDeleted.value) {
    showDeleteAttributeModal.value = true;
  } else {
    if (isHolidayUsedInFlows.value) {
      await loadFlows();
    }
    showDeleteAttributeModal.value = true;
  }
}

async function deleteHoliday() {
  showDeleteAttributeModal.value = false;
  if (canSelectedHolidayBeDeleted.value) {
    try {
      await executeDelete(selectedHoliday.value.id!);
      Toast.showSuccess(`Successfully deleted holiday: ${selectedHoliday.value.name}`);
    } catch (err) {
      Toast.showError(err.message);
    }
  }
  selectedHoliday.value = {} as HolidayViewModel;
}

function getColumns(): DataTableColumn[] {
  return [
    {
      display: 'Name',
      name: 'name',
      clickable: true,
    },
    {
      display: 'When',
      name: 'dates',
      formatter: formatWhen,
    },
  ];
}

function loadBusinessHours() {
  return botsApi.getBusinessHours(currentWorkspace.value.id);
}

function openingTimesError($event: any) {
  invalidOpeningTimes.value = $event;
}

function save() {
  if (updateInProgress.value || isLoading.value || botAppInstallError.value) {
    return;
  }
  updateInProgress.value = true;

  const mappedHours = businessHours.value.map((val) => {
    if (val.closed) {
      val.startTime = undefined;
      val.endTime = undefined;
    }
    return val;
  });

  botsApi
    .updateBusinessHours(currentWorkspace.value.id, mappedHours)
    .then(() => {
      Toast.showSuccess('Successfully updated Bot');
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      updateInProgress.value = false;
    });
}

function formatWhen(dates: Date[]): string {
  if (dates) {
    const datesToDisplay = dates.slice(0, 3);
    const readableDates = datesToDisplay.map((date) => moment(date).format('MMMM Do YYYY')).join(', ');
    return dates.length > 3 ? `${readableDates}...` : readableDates;
  }
  return '';
}

function getFlowDisplayName(flowId: string): string {
  const flow = getFlow.value(flowId);
  return flow ? HtmlFormatter.escapeHtmlCharacters(flow.name) : flowId;
}
</script>

<style lang="scss" scoped>
.holidays-section {
  overflow: visible;

  :deep(.content-container) {
    padding: 12px 0.5em;
  }
}

.business-hours-section {
  .business-hour-selection {
    max-width: max-content;
  }

  :deep(.openingTimes-row .select-input) {
    flex: 0 1 auto;
  }

  :deep(.toggle-container .toggle-label__text--online) {
    display: block;
  }
}

section {
  flex: 1;
}

.dropdown-item {
  .action-label {
    margin-left: 1em;
  }
}

.button-container {
  margin: 0.5rem 0;
  text-align: right;
}

.no-holidays {
  color: $color-netcall-body-text;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  height: 15vh;

  .no-holidays-message {
    display: flex;
  }

  .material-design-icon {
    margin: 10px;
  }
}

:deep(#confirmation-message) {
  ul {
    list-style: disc outside;
    margin-left: 2em;
    margin-top: 1em;
  }
}

.holidays-section :deep(.header.columns) {
  align-items: center;
}
</style>
